import type { ReactNode } from "react";

function H2({ children }: { children: ReactNode }) {
	return (
		<h2
			className="mt-10 font-mono text-[clamp(1.15rem,2.2vw,1.4rem)] leading-[1.2] tracking-[-0.01em] text-foreground"
			style={{ fontWeight: 400 }}
		>
			{children}
		</h2>
	);
}

function H3({ children }: { children: ReactNode }) {
	return (
		<h3 className="mt-7 font-mono text-base leading-[1.3] text-foreground">
			{children}
		</h3>
	);
}

function P({ children }: { children: ReactNode }) {
	return (
		<p className="mt-4 text-base leading-[1.65] text-muted-foreground">
			{children}
		</p>
	);
}

function Note({ children }: { children: ReactNode }) {
	return (
		<div className="mt-6 border border-border bg-card p-4">
			<p className="text-base leading-[1.6] text-muted-foreground">
				{children}
			</p>
		</div>
	);
}

export function TrustGapPost() {
	return (
		<div>
			<P>
				Agent demos land well. You show a thing that reads an inbox, decides
				something and acts, and the room goes quiet in the good way. Then the
				follow-up email goes unanswered, the pilot never starts, and the deal
				that felt inevitable in the demo does not close.
			</P>
			<P>
				The instinct is to read that as a capability problem and go build more.
				Usually it is not. The demo already proved the agent can do the work.
				What it did not settle is who carries the cost when the agent does the
				work wrong. That question is the trust gap, and no amount of additional
				capability closes it.
			</P>

			<H2>&gt; The demo converts attention, not budget</H2>
			<P>
				A demo is evaluated on whether the thing works. A purchase is evaluated
				on what happens when it does not. Those are different questions and they
				are usually asked by different people. The person who loved the demo is
				rarely the person who has to explain a bad outcome to a customer.
			</P>
			<P>
				So the honest read on a stalled pilot is not that the buyer was
				unimpressed. It is that somewhere between the demo and the contract,
				someone asked what happens if this sends the wrong thing to a client,
				and nobody had a good answer.
			</P>

			<H2>&gt; What buyers are actually pricing</H2>
			<P>
				Buyers are not pricing accuracy in the abstract. They are pricing the
				worst plausible outcome multiplied by how often it happens and divided
				by how quickly they would notice.
			</P>
			<P>
				That formula explains a lot of otherwise confusing purchasing behaviour.
				An agent that is right 95% of the time is an easy yes when the 5% is a
				slightly awkward draft nobody sends. The same 95% is an easy no when the
				5% is a wrong price quoted to a customer in writing. Same model, same
				accuracy, opposite decisions, because the worst case is not the same
				shape.
			</P>
			<Note>
				This is why accuracy benchmarks rarely move enterprise deals. A buyer
				who cannot bound the downside does not care much where you sit between
				93% and 97%.
			</Note>

			<H2>&gt; Three things that actually close the gap</H2>

			<H3>1. Narrow the blast radius</H3>
			<P>
				Scope what the agent is allowed to touch, and be specific in the product
				rather than in the sales call. Read everything, write to a draft, send
				to nobody is a posture a buyer can evaluate in one sentence. Full access
				to your systems with sensible guardrails is not, because guardrails is
				not a noun anyone can check.
			</P>
			<P>
				The counterintuitive part is that a narrower agent often sells better
				than a broader one at the same price. Narrow is legible. Broad requires
				trust you have not earned yet. If that boundary includes third-party
				tools, use the{" "}
				<a
					href="/blog/mcp-server-security-checklist"
					className="text-foreground underline underline-offset-4 transition-colors hover:text-brand"
				>
					MCP server security checklist
				</a>{" "}
				before you connect one to live data.
			</P>

			<H3>2. Put a person in the send path</H3>
			<P>
				The single most effective structural answer is that the agent drafts and
				a human approves. It costs some of the magic and it removes the entire
				category of objection that was blocking the deal.
			</P>
			<P>
				Products are increasingly shipping this as the default rather than a
				setting.{" "}
				<a
					href="https://wysera.ai/"
					className="text-foreground underline underline-offset-4 transition-colors hover:text-brand"
				>
					Wysera's approval-first model
				</a>{" "}
				is a clear example, where the agent drafts the work and a person
				approves it, autonomy is opt-in per category rather than granted
				wholesale, and every action carries an audit trail with one-click
				rollback. The design choice worth copying is the direction of the
				default. Approval is what happens unless the customer deliberately turns
				it off for a category they have decided to trust.
			</P>
			<P>
				This also gives you a real expansion path. Customers who start fully
				gated and later switch categories to autonomous have told you exactly
				which parts of the product earned trust, which is better roadmap
				information than any survey.
			</P>

			<H3>3. Make the work inspectable</H3>
			<P>
				An agent that cannot show its work is asking for faith. Log what it
				read, what it decided and what it changed, in language the buyer's team
				can follow without opening a terminal. Then make the reversal one action
				rather than a support ticket.
			</P>
			<P>
				Reversibility is worth more than it looks. A mistake you can undo in ten
				seconds is an annoyance. The same mistake with no undo is an incident,
				and buyers price incidents.
			</P>

			<H2>&gt; Autonomous is a weak thing to lead with</H2>
			<P>
				Fully autonomous reads as a capability claim to engineers and as a risk
				disclosure to everyone else. It tells a buyer that the thing will act
				without asking, which is precisely the sentence their risk instinct was
				waiting for.
			</P>
			<P>
				Lead with the outcome and the bound instead. It handles the follow-up
				and nothing leaves without your yes is a stronger opening than it runs
				itself, and it is the same product.
			</P>

			<H2>&gt; Proof beats positioning</H2>
			<P>
				Everything above is design. The other half is evidence, and evidence is
				harder to fake than a landing page. A buyer deciding whether to trust an
				agent with real work wants to know that other people already pay for it,
				and that somebody checked.
			</P>
			<P>
				This is the part founders tend to underinvest in, because it feels like
				marketing rather than product. It is neither. It is the thing that makes
				the rest of your claims survivable.
			</P>

			<H2>&gt; Frequently asked questions</H2>

			<H3>Does an approval step defeat the point of an agent?</H3>
			<P>
				It removes the drafting time, which is where nearly all the delay lives.
				It does not remove the decision. Most customers were never bothered by
				spending ten seconds saying yes. They were bothered by spending twenty
				minutes writing the thing.
			</P>

			<H3>When is full autonomy reasonable?</H3>
			<P>
				When the worst case is small, reversible and visible quickly. Sorting,
				tagging, drafting and internal routing usually qualify. Anything that
				talks to a customer, moves money or changes a public record usually does
				not, at least not before the account has a track record.
			</P>

			<H3>How do I know the trust gap is what is blocking me?</H3>
			<P>
				Look at where deals die. If prospects are enthusiastic and then go quiet
				after an internal conversation you were not in, that is the shape.
				Losing on features usually sounds like losing on features, because
				people say so.
			</P>

			<H2>&gt; Where AgentMRR fits</H2>
			<P>
				AgentMRR is a public board of AI agents whose revenue has been verified
				through their payment provider rather than self-reported. Listings
				become public once a provider is connected, so what you see is what
				somebody is actually being paid.
			</P>
			<P>
				That is the proof half of this article made into infrastructure. If you
				are building an agent and the trust gap is what is holding your pipeline
				up, a verified revenue figure next to your name is a cheap thing to add
				and a hard thing to argue with. Listing takes one HTTP request and no
				account, described in the{" "}
				<a
					href="/docs/submit"
					className="text-foreground underline underline-offset-4 transition-colors hover:text-brand"
				>
					submit docs
				</a>
				.
			</P>
		</div>
	);
}
